import { cn } from "@/lib/utils"

/**
 * A 12px strip pinned to the top of the viewport, drawn in the top bar's own
 * colour, that tells iOS 26 to give the top of the page a hard edge instead
 * of a Liquid Glass blur.
 *
 * WebKit (`LocalFrameView::fixedContainerEdges`) hit-tests the point 4px
 * below the top-centre of the viewport, walks up from whatever it hits and
 * takes the first `fixed`/`sticky` box that is 90–105% of the viewport wide,
 * over 10px tall, opaque enough and painted in a plain `background-color`.
 * Found → flat edge in that colour; not found → a ~40pt progressive blur
 * over the app's top bar. A full-width sticky header with a plain
 * background passes alone, but one beside a docked sidebar is too narrow.
 * This strip is always viewport-wide (fixed, no transformed ancestor), so the
 * test passes whatever the bar's width. 12% opacity keeps it a faint wash of
 * the bar's own colour where it overlaps content; WebKit reads the colour
 * from style, not pixels, and only rejects opacity under 0.1.
 *
 * EXACTLY ONE IS MOUNTED, for the whole app (`persistent`, in App.tsx). WebKit
 * samples the top edge only while the page is loading or until the reader's
 * first tap or scroll (`Page::updateFixedContainerEdges`); after that the
 * colour is frozen for the page load and kept only while the element it came
 * from is still in the DOM. So the strip never unmounts, and it sits on the
 * `edge` rung: above the app header (`chrome`) and the service banner
 * (`banner`), above the sign-in frame's bar and card, below every overlay —
 * so it is what every sample finds. Its colour is `bg-background`, the app
 * header's ground; on the sign-in screens `html.auth-backdrop-on` repaints it
 * in the film's ink, the colour of AuthPageShell's bar (index.css). The
 * sign-in → app boundary and sign-out are real page loads
 * (lib/landAfterSignIn.ts), so each side is sampled fresh in its own colour.
 *
 * There used to be a copy inside each pinned header too, "as backups". They
 * were not harmless: at 12% opacity a copy washes whatever in-flow band sits
 * at the top of its bar — a banner, an iPadOS window-controls band — and read
 * as banding at the top of every screen (standing rule, 2026-09-29). One
 * strip, colour-matched; tests/topEdgeSample.test.ts allows it in App.tsx
 * only.
 */
export function TopEdgeSample({
  className = "bg-background",
  persistent = false,
}: {
  className?: string
  /** The root strip (App.tsx): above every top bar, never unmounted. */
  persistent?: boolean
}) {
  return (
    <div
      aria-hidden="true"
      data-top-edge-sample={persistent ? "root" : ""}
      className={cn(
        "pointer-events-none fixed inset-x-0 top-0 h-3 opacity-[.12]",
        persistent ? "z-edge" : "z-0",
        className,
      )}
    />
  )
}
